import { Tooltip, useDisclosure } from "@chakra-ui/react";
import React from "react";
import { IoArrowRedoOutline } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";

import { IconButton } from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { AiInterviewCallFragment, CandidateLinkType } from "../../graphql";
import { DetailsHeader } from "../AiRecording/DetailsHeader";
import useCallMenuOptions from "../InterviewDetails/useCallMenuOptions";
import { ATSLinks, CandidateLinks } from "../Recording";
import { ShareCallButton } from "../ShareCallButton";

type AiInterviewHeaderProps = {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "type"
    | "candidate"
    | "position"
    | "startTime"
    | "status"
    | "visibility"
    | "visibilityLevels"
    | "canEdit"
    | "canDelete"
    | "canShare"
    | "canShareExternal"
    | "skipRedaction"
  > & { aiInterviewer: NonNullable<AiInterviewCallFragment["aiInterviewer"]> };
  actionButtons?: JSX.Element;
};

/**
 * Wraps AiRecording/DetailsHeader component
 */
const AiInterviewHeader: React.FC<AiInterviewHeaderProps> = ({
  call,
  actionButtons,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const navigate = useNavigate();
  const location = useLocation();

  const setPath = (path: string): void =>
    navigate(
      {
        pathname: `/interview/${call.id}${path}`,
        search: window.location.search,
      },
      { replace: true }
    );

  const candidateLinks: ATSLinks = {};

  const linkedIn = call.candidate?.candidateLinks.find(
    ({ type }) => type === CandidateLinkType.Linkedin
  )?.link;

  if (linkedIn) {
    candidateLinks.linkedIn = { href: linkedIn, tooltipLabel: "LinkedIn" };
  }

  const menuOptions = useCallMenuOptions(call);

  const shareModal = useDisclosure({
    isOpen: location.pathname.endsWith("/share"),
    onOpen: () => setPath("/share"),
    onClose: () => setPath(""),
  });

  return (
    <DetailsHeader
      candidate={call.candidate}
      position={call.position}
      aiInterviewer={call.aiInterviewer}
      date={call.startTime}
      candidateLinks={
        isSmallScreen ? null : <CandidateLinks {...candidateLinks} />
      }
      share={
        call.canShare || call.canShareExternal ? (
          <ShareCallButton
            callId={call.id}
            positionId={call.position?.id}
            callVisibility={call.visibility}
            visibilityLevels={call.visibilityLevels}
            readOnly={!call.canEdit}
            isModalOpen={shareModal.isOpen}
            onOpenModal={shareModal.onOpen}
            onCloseModal={shareModal.onClose}
          >
            <Tooltip label="Share Interview" openDelay={1000}>
              <IconButton
                icon={<IoArrowRedoOutline size="20" />}
                aria-label="Share Interview"
                data-testid="share-button"
                color="gray.900"
                variant="white"
                minW="8"
                h="8"
                ml="2"
              />
            </Tooltip>
          </ShareCallButton>
        ) : undefined
      }
      menuOptions={menuOptions}
      actionButtons={actionButtons}
    />
  );
};

export default AiInterviewHeader;
